import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DateTime } from 'luxon';

import { SvgIcon } from 'app/shared/svg-icon/svg-icon';
import { Select } from 'app/shared/atoms/select/select';
import { EmptyDataParticules } from 'app/shared/molecules/empty-data-particules/empty-data-particules';
import { ClosedMonth, SalesSummary } from 'app/core/models/sales-summary.model';
import { deltaVisual } from 'app/shared/utils/monthly-delta';
import { formatCurrencyAmount, signedCurrency } from 'app/shared/utils/sales-funnel';
import {
  DEFAULT_DASHBOARD_PERIOD,
  dashboardPeriodOptions,
  dashboardWindowLabel,
} from 'app/home/dashboard-period';

interface Bar {
  /** 0-100 height of the orders bar, relative to the tallest visible value. */
  height: number;
  /** 0-100 height of the approved-quotes point, on the same scale. */
  lineHeight: number;
  /** True once the approved series has started — before that the line is absent. */
  hasLine: boolean;
  /** Axis tick: "ene", "feb"… The year is stated once, by the period selector. */
  label: string;
  /** Tooltip heading: the month spelled out with its year. */
  fullLabel: string;
  orderAmount: number;
  approvedAmount: number;
  /** Preformatted for the tooltip, so hovering does no work. */
  orderLabel: string;
  approvedLabel: string;
  current: boolean;
  /** 0-100 height of the same month a year earlier, on the same scale. */
  previousHeight: number;
  /** The baseline's amount, formatted, or null when there is no comparison. */
  previousLabel: string | null;
  /**
   * Query params for the link into the sales-orders listing: this month's
   * inclusive bounds plus the currency the bar was summed in.
   *
   * Built here rather than composed in the template because Angular template
   * expressions have no object spread, and the currency is the same for every
   * bar while the dates are not.
   */
  linkParams: Record<string, string>;
}

/** Floor so a month with sales never draws as an invisible sliver. */
const MIN_ACTIVE_HEIGHT = 6;

/**
 * Closed business per month: sales orders as bars, approved quotes as an
 * overlaid line.
 *
 * The line legitimately starts partway across. `Offer.ClosedDate` shipped on
 * 2026-08-06 and is only written on transition into a closed status, so quotes
 * approved before then carry no date and contribute nothing. Substituting
 * `CreatedDate` would label a quote's creation as its approval, so the card
 * shows what is real and names the month the line starts.
 */
@Component({
  selector: 'primecontrol-closed-months-card',
  imports: [CommonModule, RouterModule, TranslateModule, SvgIcon, Select, EmptyDataParticules],
  templateUrl: './closed-months-card.html',
  styleUrl: './closed-months-card.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClosedMonthsCard {
  readonly summary = input<SalesSummary | null>(null);
  readonly loading = input<boolean>(false);
  /** The summary request failed: an error state, never the "no data" one. */
  readonly error = input<boolean>(false);

  /**
   * The same window a year earlier, drawn as a hollow bar behind each month.
   *
   * A chart of one year is a record; the same chart against last year is a
   * judgement. Optional throughout — an instance in its first year, or a slow or
   * forbidden second request, simply gets no ghosts.
   */
  readonly previous = input<SalesSummary | null>(null);

  /**
   * The chosen window, emitted so the page can re-request the summary.
   *
   * This card used to carry a dropdown of its own that only sliced the array it
   * had already been given — the chart zoomed, the numbers behind it did not,
   * and nothing else on the dashboard moved. The control now asks the server for
   * a different window, and the whole block follows.
   */
  readonly periodChange = output<string>();

  protected readonly periodOptions = dashboardPeriodOptions();
  protected readonly periodControl = new FormControl<string>(DEFAULT_DASHBOARD_PERIOD);

  /** Index of the column under the pointer, or null. Drives the tooltip and the
   *  dimming of every other column. */
  protected readonly hovered = signal<number | null>(null);

  /**
   * Touch screens have no hover, so a tap on a column would navigate before the
   * readout could show its month. There the first tap only reads the column
   * (arms it) and a second tap on the same column follows the link.
   */
  private readonly coarsePointer =
    typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;

  /** Column already read by a first tap on a touch screen, or null. */
  protected readonly armed = signal<number | null>(null);

  private _translateService: TranslateService = inject(TranslateService);
  private _langChange = toSignal(this._translateService.onLangChange);

  private readonly lang = computed<string>(() => {
    this._langChange();
    return this._translateService.currentLang || this._translateService.defaultLang || 'es';
  });

  private readonly isoCode = computed<string>(
    () => this.summary()?.currencies?.find((c) => c.isPrimary)?.isoCode ?? ''
  );

  private readonly allMonths = computed<ClosedMonth[]>(() => this.summary()?.closedByMonth ?? []);

  protected readonly hasData = computed<boolean>(() =>
    this.allMonths().some((m) => m.orderAmount > 0 || m.approvedAmount > 0)
  );

  /** Last year's buckets, aligned to this year's by position — both windows are
   *  the same length, so index i is the same month twelve months apart. */
  private readonly previousMonths = computed<ClosedMonth[]>(
    () => this.previous()?.closedByMonth ?? []
  );

  /**
   * Tallest value in the window — the top of the scale, shared by the bars and
   * the axis so a label always names the height beside it.
   *
   * The comparison counts towards it. Scaling to this year alone would let a
   * bigger last year run off the top of the chart, and a baseline you cannot see
   * the top of is worse than no baseline.
   */
  private readonly maxAmount = computed<number>(() =>
    Math.max(
      ...this.allMonths().map((m) => Math.max(m.orderAmount, m.approvedAmount)),
      ...this.previousMonths().map((m) => m.orderAmount),
      0
    )
  );

  /**
   * What the comparison window is called, for the legend: "2025", or a range
   * when it straddles two years. Null when nothing came back to compare.
   */
  protected readonly comparisonLabel = computed<string | null>(() => {
    const months = this.previousMonths();
    if (!months.length) {
      return null;
    }
    const lang = this.lang();
    const first = months[0];
    const last = months[months.length - 1];
    if (first.year === last.year) {
      return `${first.year}`;
    }
    const start = DateTime.fromObject({ year: first.year, month: first.month }).setLocale(lang);
    const end = DateTime.fromObject({ year: last.year, month: last.month }).setLocale(lang);
    return `${start.toFormat('LLL yyyy')} – ${end.toFormat('LLL yyyy')}`;
  });

  /**
   * Top of the drawn scale: the tallest value rounded UP to a round step.
   *
   * Normalising to the raw maximum produced an axis of "208,2 mil / 156,1 mil /
   * 104,1 mil / 52 mil" — four figures nobody can hold in their head, derived
   * from a number that happens to be a month's sales. Rounding to a step of 1,
   * 2 or 5 times a power of ten gives "250 mil / 200 mil / 150 mil…", and the
   * tallest bar stops short of the ceiling, which is what tells the eye the
   * scale is a scale and not a crop.
   */
  private readonly scaleStep = computed<number>(() => niceStep(this.maxAmount()));

  private readonly scaleMax = computed<number>(() => {
    const step = this.scaleStep();
    const max = this.maxAmount();
    return step <= 0 ? 0 : step * Math.max(1, Math.ceil(max / step));
  });

  /**
   * Y axis: one tick per round step up to the top of the scale, positioned by
   * the same percentages the gridlines use.
   *
   * Compact figures ("200 mil"), not full currency — several amounts written out
   * in full would outweigh the chart they annotate, and the exact number is one
   * hover away. Empty while there is no money at all: an axis of zeros is a
   * scale that measures nothing. Zero itself is not listed; the baseline is it.
   */
  protected readonly axisTicks = computed<{ pct: number; label: string }[]>(() => {
    const step = this.scaleStep();
    const top = this.scaleMax();
    if (step <= 0 || top <= 0) {
      return [];
    }
    const lang = this.lang();
    const ticks: { pct: number; label: string }[] = [];
    for (let value = top; value > 0; value -= step) {
      ticks.push({ pct: ((top - value) / top) * 100, label: compactAmount(value, lang) });
    }
    return ticks;
  });

  /**
   * One column per month in the requested window — no local slicing. The window
   * is whatever the period selector asked the server for, so every bucket the
   * payload carries belongs on screen.
   */
  protected readonly bars = computed<Bar[]>(() => {
    const months = this.allMonths();
    const previous = this.previousMonths();
    // Heights normalise to the rounded ceiling, not the raw maximum, so the
    // bars line up with the axis figures beside them.
    const max = this.scaleMax();
    const firstLineIndex = months.findIndex((m) => m.approvedAmount > 0);
    const lang = this.lang();
    const iso = this.isoCode();
    const currencyId = this.summary()?.currencies?.find((c) => c.isPrimary)?.idCurrency ?? null;
    // Only when the two windows are the same length: a mismatch means the
    // comparison request answered a different question, and lining the buckets
    // up by position would compare January against March without saying so.
    const comparable = previous.length === months.length;

    return months.map((month, index) => {
      const at = DateTime.fromObject({ year: month.year, month: month.month }).setLocale(lang);
      const before = comparable ? previous[index] : null;
      return {
        previousHeight: before ? barHeight(before.orderAmount, max) : 0,
        previousLabel: before ? formatCurrencyAmount(before.orderAmount, lang, iso) : null,
        linkParams: {
          from: at.startOf('month').toFormat('yyyy-MM-dd'),
          to: at.endOf('month').toFormat('yyyy-MM-dd'),
          // The bar sums ONE currency; without this the listing would show
          // orders the bar never counted, and its total would not match.
          // `idCurrency` filters, `currency` is what the listing's chip prints —
          // its rows carry no currency of their own.
          ...(currencyId != null ? { idCurrency: `${currencyId}`, currency: iso } : {}),
        },
        height: barHeight(month.orderAmount, max),
        lineHeight: barHeight(month.approvedAmount, max),
        hasLine: firstLineIndex >= 0 && index >= firstLineIndex,
        // The tick drops the year: within one window every column shares it, and
        // the selector above already names it. "sept 25 … ago 26" was the old
        // label, and a reader could not tell which year any middle bar was in.
        label: at.toFormat('LLL'),
        fullLabel: at.toFormat('LLLL yyyy'),
        orderAmount: month.orderAmount,
        approvedAmount: month.approvedAmount,
        orderLabel: formatCurrencyAmount(month.orderAmount, lang, iso),
        approvedLabel: formatCurrencyAmount(month.approvedAmount, lang, iso),
        current: index === months.length - 1,
      };
    });
  });

  /**
   * The window these bars actually cover, spelled out. Shared with the other
   * cards of this block — see `dashboardWindowLabel` for why it is read off the
   * data rather than off the chosen period.
   *
   * It matters most here: the ticks drop the year (every column shares it) and
   * the selector says "Año en curso", so without this line nothing on the card
   * names a year, and a window that crosses New Year names none at all.
   */
  protected readonly windowLabel = computed<string>(() =>
    dashboardWindowLabel(this.allMonths(), this.lang())
  );

  /** The column being pointed at. */
  protected readonly hoveredBar = computed<Bar | null>(() => {
    const index = this.hovered();
    return index == null ? null : (this.bars()[index] ?? null);
  });

  /**
   * The readout strip above the chart: the hovered month, or the window's
   * totals when nothing is being pointed at.
   *
   * A fixed row rather than a floating tooltip. Floating it meant covering the
   * bars — including the one being read — and reserving blank space above them
   * for it would have been space that says nothing four fifths of the time.
   * With a default the strip always carries a figure worth having, and the
   * chart below it is never occluded.
   */
  protected readonly readout = computed<{
    /** i18n key qualifying the month, or null when it is simply the one hovered. */
    caption: string | null;
    month: string;
    orderLabel: string;
    approvedLabel: string;
    previousLabel: string | null;
  } | null>(() => {
    const bar = this.hoveredBar();
    if (bar) {
      return {
        caption: null,
        month: bar.fullLabel,
        orderLabel: bar.orderLabel,
        approvedLabel: bar.approvedLabel,
        previousLabel: bar.previousLabel,
      };
    }

    // At rest: the best month, not the window's total. The total is the headline
    // now, and a strip that repeats the number above it is a strip that costs a
    // row and says nothing.
    const best = this.bestBar();
    if (!best) {
      return null;
    }
    return {
      caption: 'PERIOD_BEST_MONTH',
      month: best.fullLabel,
      orderLabel: best.orderLabel,
      approvedLabel: best.approvedLabel,
      previousLabel: best.previousLabel,
    };
  });

  /** Orders closed across the whole window — what the selector promises. */
  private readonly periodTotal = computed<number>(() =>
    this.allMonths().reduce((acc, m) => acc + m.orderAmount, 0)
  );

  private readonly previousTotal = computed<number>(() =>
    this.previousMonths().reduce((acc, m) => acc + m.orderAmount, 0)
  );

  /**
   * The headline: everything closed in the chosen window.
   *
   * It used to report the LAST month of the window, which under "año en curso"
   * is the current one — mid-month and usually empty. The biggest number on the
   * card read 0 while the chart underneath showed 208 mil in February. A card
   * whose control says "this year" has to answer for this year.
   */
  protected readonly heroLabel = computed<string>(() =>
    formatCurrencyAmount(this.periodTotal(), this.lang(), this.isoCode())
  );

  /**
   * The badge beside the headline: this window against the same window a year
   * earlier — the comparison the ghost bars already draw.
   *
   * Month-over-month belonged to a headline that was a month. Against a period
   * total it would have compared a year to a month. Null when nothing came back
   * to compare, so the badge is absent rather than claiming a rise from zero.
   *
   * Arrow and ink come from `deltaVisual`, the same rule the metric tiles use:
   * emerald only for a gain, neutral otherwise.
   */
  protected readonly delta = computed<{
    label: string;
    icon: string;
    classes: string;
    tooltipParams: Record<string, string>;
  } | null>(() => {
    if (!this.previousMonths().length) {
      return null;
    }
    const lang = this.lang();
    const iso = this.isoCode();
    const current = this.periodTotal();
    const previous = this.previousTotal();

    return {
      ...deltaVisual(current - previous),
      label: signedCurrency(current - previous, lang, iso),
      tooltipParams: {
        currentWindow: this.windowLabel(),
        previousWindow: this.comparisonLabel() ?? '',
        currentAmount: formatCurrencyAmount(current, lang, iso),
        previousAmount: formatCurrencyAmount(previous, lang, iso),
      },
    };
  });

  /** The month that closed the most in this window — what the readout falls back
   *  to now that the total moved up to the headline. */
  private readonly bestBar = computed<Bar | null>(() => {
    const bars = this.bars().filter((bar) => bar.orderAmount > 0);
    if (!bars.length) {
      return null;
    }
    return bars.reduce((best, bar) => (bar.orderAmount > best.orderAmount ? bar : best));
  });

  /**
   * The month the approved-quotes line starts, or null when it never does
   * inside this window — so the legend can only ever name a month the chart is
   * actually showing.
   */
  protected readonly lineStartLabel = computed<string | null>(() => {
    const first = this.bars().find((bar) => bar.hasLine);
    return first ? first.label : null;
  });

  /**
   * Link target of a column: the sales-orders listing for a month with orders.
   *
   * On a touch screen it stays null until the column has been armed by a first
   * tap. Gating the link itself is required because RouterLink ignores
   * `preventDefault()` and navigates on any click while it has a target.
   *
   * @param bar The column's data.
   * @param index The column's position in `bars()`.
   */
  protected barLink(bar: Bar, index: number): string | null {
    if (bar.orderAmount <= 0) {
      return null;
    }
    if (this.coarsePointer && this.armed() !== index) {
      return null;
    }
    return '/sales/sales-orders';
  }

  /**
   * First tap on a touch screen: show the column's month in the readout and
   * keep the tap from navigating. A mouse click, or a second tap on the same
   * column, falls through to the link.
   *
   * @param event The click on the column.
   * @param index The column's position in `bars()`.
   */
  protected onBarClick(event: MouseEvent, index: number): void {
    if (!this.coarsePointer || this.armed() === index) {
      return;
    }
    event.preventDefault();
    this.hovered.set(index);
    this.armed.set(index);
  }

  /** The pointer left the plot: nothing is read and nothing stays armed. */
  protected onPlotLeave(): void {
    this.hovered.set(null);
    this.armed.set(null);
  }

  /** The page owns the period; this only reports the pick. */
  protected onPeriodChange(): void {
    this.hovered.set(null);
    this.armed.set(null);
    this.periodChange.emit(this.periodControl.value ?? DEFAULT_DASHBOARD_PERIOD);
  }
}

/**
 * Bar height as a percentage of the tallest visible month, floored so a month
 * with sales is never invisible.
 *
 * @param value Amount closed in the month.
 * @param max Amount closed in the tallest visible month.
 */
function barHeight(value: number, max: number): number {
  if (value <= 0 || max <= 0) {
    return 0;
  }
  return Math.max(MIN_ACTIVE_HEIGHT, Math.round((value / max) * 100));
}

/**
 * Axis figure: "40 k", "1,2 M". Falls back to a plain grouped number where the
 * runtime has no compact notation.
 *
 * @param value Amount at that tick.
 * @param lang Active i18n language.
 */
/**
 * A round axis step near a quarter of the range: 1, 2 or 5 times a power of ten.
 *
 * @param max Tallest value the chart has to fit.
 */
function niceStep(max: number): number {
  if (max <= 0) {
    return 0;
  }
  const rough = max / 4;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rough)));
  const normalised = rough / magnitude;
  const factor = normalised <= 1 ? 1 : normalised <= 2 ? 2 : normalised <= 5 ? 5 : 10;
  return factor * magnitude;
}

function compactAmount(value: number, lang: string): string {
  try {
    return new Intl.NumberFormat(lang, {
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(value);
  } catch {
    return new Intl.NumberFormat(lang, { maximumFractionDigits: 0 }).format(value);
  }
}
